import './App.css';
import {useLayoutEffect, useState, useRef, useEffect, createContext, useContext} from 'react';
import {useLocation} from 'react-router-dom'
import {csrfToken, fetchCsrfToken, postWithCsrf, sanitizeLocaleMessages} from './util';
import { ScrollBarAdapter } from './ScrollBarAdapter';
import {system} from "./globals";
import { LoadState, Notifications } from './enums';
import { ClosePopup, LoadButton, Loading } from './Common';
import { useNavigateSmart, useResizeListener } from './hooks';

// Create a Context
export const LocaleContext = createContext();
export const ScrollContext = createContext();
export const OverlayContext = createContext();
export const StateContext = createContext();
export const LoginContext = createContext();
export const NavigateLockContext = createContext();

function App(props) {
    const location = useLocation();
    const navigate = useNavigateSmart();

    const boardRef = useRef();
    const appBoxRef = useRef();
    const [boardHeight, setBoardHeight] = useState(0);
    const boardHeightRef = useRef(boardHeight);
    const [innerWidth, setInnerWidth] = useState(0);
    const [displaySystemLabel, setDisplaySystemLabel] = useState(true);
    const [loadState, setLoadState] = useState(LoadState.LOADING);
    const [contentHeight, setContentHeight] = useState(0);

    const scrollDummyRef = useRef();
    const localeRef = useRef(null);
    const initRef = useRef(true);
    const scrollLimitYRef = useRef(-1);
    const scrollLimitTimeout = useRef(null);
    const contentRef = useRef();


    // For OverlayContext
    const [overlay, setOverlay] = useState(false);
    const [overlaySolid, setOverlaySolid] = useState(false);
    const [overlaySolidWhite, setOverlaySolidWhite] = useState(false);
    const [loadingCounter, setLoadingCounter] = useState(0);
    const [errorPopupInfo, setErrorPopupInfo] = useState({"title": "", "text": ""});
    const [errorPopupDisplayed, setErrorPopupDisplayed] = useState(false);

    // For LocaleContext
    const [locale, setLocale] = useState("de");
    const [msgs, setMsgs] = useState({});

    // For ScrollContext
    const scrollOffset = useRef(0);
    const curScrollPos = useRef();

    // For LoginContext
    const [checkingLogin, setCheckingLogin] = useState(true);
    const [loggedIn, setLoggedIn] = useState(false);
    const [userType, setUserType] = useState("");
    const [mailConfirmed, setMailConfirmed] = useState("");
    const [twoFactorAuthEnabled, setTwoFactorAuthEnabled] = useState("");
    const [notifications, setNotifications] = useState(Notifications.NONE);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [twoFactorAuthType, setTwoFactorAuthType] = useState("");
    const [twoFactorAuthMobileNo, setTwoFactorAuthMobileNo] = useState(null);
    const [nextSmsCountS, setNextSmsCountS] = useState(-1);
    const [noContentPad, setNoContentPad] = useState(false);
    const [scrollOnAppBox, setScrollOnAppBox] = useState(false);
    const [noScroll, setNoScroll] = useState(false);  // Useful for fixed content that also overflows
    const navigateUrlAfterLogin = useRef(null);  // A URL to navigate after login
    const [displayRememberDevice, setDisplayRememberDevice] = useState(false);
    const [remLoadState, setRemLoadState] = useState(LoadState.NONE);

    // For NavigateLockContext
    const lockedRef = useRef(false);
    const [locked, setLocked] = useState(false);
    const [lockAnimationRunning, setLockAnimationRunning] = useState(false);


    // For StateContext
    const fetching = useRef();


    // FUNCTIONS:

    // If set, it will be automatically scrolled to the set limit if it gets exceeded
    // limitY should be relative to the viewport
    function setScrollLimitY(limitY) {
        scrollLimitYRef.current = limitY;
    }

    function setLockedFunc(l) {
        lockedRef.current = l;
        setLocked(l);
    }

    function handleLockProtectedClick(e, onClick) {
        if(lockedRef.current) {
            setLockAnimationRunning(true);
        }
        else {
            onClick(e);
        }
    }

    function getScrollEle() {
        let scrollEle = document.documentElement;

        if(scrollOnAppBoxFunc()) {
            scrollEle = appBoxRef.current;
        }

        return scrollEle;
    }

    // Only scrolls if any part of the element is not visible
    function scrollTo(top, bottom, smooth=true, toTop=false) {
        // WARNING: Do not use a dynamic scroll dummy as it will trigger a rerender of this App which causes all components to be rerendered.
        // This is not just creating overhead but also triggers an initial execution of effect hooks which might be undesired (e.g. DropdownInfo will open
        // again, also if just closed)

        const scrollEle = getScrollEle();

        if(!scrollEle) {
            return;
        }

        // We want to scroll e.g. above any "position: fixed" footer (e.g. buttons / tabs)
        bottom += scrollOffset.current;

        // const scrollEleRect = scrollEle.getBoundingClientRect();
        const scrollDummyRect = scrollDummyRef.current.getBoundingClientRect();

        const topCalculatedFromTop = top - scrollDummyRect.top - 15;

        if(top < 0) {
            scrollEle.scrollTo({top: topCalculatedFromTop, behavior: (smooth ? "smooth" : "auto") });
            // scrollRef.current.scrollTo(0, top - scrollDummyEleRect.top - 15);
        }

        if(bottom > scrollEle.clientHeight) {
            // console.log(bottom + ", " + scrollEle.clientHeight);

            const topCalculatedFromBottom = bottom - scrollDummyRect.top - scrollEle.clientHeight + 15;
            
            let topCalc = topCalculatedFromBottom;

            if(topCalculatedFromBottom > topCalculatedFromTop) {
                topCalc = topCalculatedFromTop;
            }

            if(toTop) {
                scrollEle.scrollTo({top: topCalculatedFromTop, behavior: (smooth ? "smooth" : "auto") });
            }
            else {
                scrollEle.scrollTo({top: topCalc, behavior: (smooth ? "smooth" : "auto") });
            }
        }
    }

    // Always scrolls
    function scrollToPlain(top, bottom, smooth=true) {
        const scrollEle = getScrollEle();

        if(!scrollEle) {
            return;
        }

        scrollEle.scrollTo({top: top, bottom: bottom, behavior: (smooth ? "smooth" : "auto") });
        scrollEle.scrollTop = top;
    }

    function scrollToPlain2(top, bottom, smooth=true) {
        const scrollEle = getScrollEle();

        if(!scrollEle) {
            return;
        }

        scrollEle.scrollTo({top: top, bottom: bottom, behavior: (smooth ? "smooth" : "auto") });
    }

    function getCurrentScrollPos() {
        return curScrollPos.current;
    }

    function addScrollListener(callback) {
        const scrollEle = getScrollEle();
        if(!scrollEle) {
            return;
        }

        scrollEle.addEventListener("scroll", callback);
    }

    function removeScrollListener(callback) {
        const scrollEle = getScrollEle();
        if(!scrollEle) {
            return;
        }

        scrollEle.removeEventListener("scroll", callback);
    }

    function stopScrollHandler(e){
        e.preventDefault();
    }

    function stopScroll() {
        document.body.addEventListener('touchmove', stopScrollHandler, { passive: false });
    }

    function startScroll() {
        document.body.removeEventListener('touchmove', stopScrollHandler);
    }

    // Do not scroll on the root as on Safari IOS the url bar at the bottom appears / disappears when scrolling which is anoying if there is a fixed box
    // (e.g. tabs) at the bottom
    function scrollOnAppBoxFunc() {
        if(scrollOnAppBox) {
            return true;
        }

        return (location.pathname.includes("collect_data") || location.pathname.includes("dashboard")) && window.innerWidth < 450;
    }

    function performCsrfFetch() {
        fetchCsrfToken().then(() => {
            setLoadState(LoadState.LOAD_SUCCESS);
            decrLoadingCounter();
        }).catch((error) => {
            console.log(error);

            setTimeout(() => performCsrfFetch(), 1000);  // Retry after 1 second
        });
    }

    function incrLoadingCounter() {
        setLoadingCounter((oldCounter) => {
            return oldCounter + 1;
        });
    }

    function decrLoadingCounter() {
        setLoadingCounter((oldCounter) => {
            return oldCounter - 1;
        });
    }

    function loadLocaleFunc(localeToLoad) {
        setLoadState(LoadState.LOADING);
        incrLoadingCounter();

        async function loadLocaleData(locale) {
            const messages = await import(`./i18n/${locale}.json`);
            setMsgs(sanitizeLocaleMessages(messages));

            performCsrfFetch();
        }
        
        loadLocaleData(localeToLoad);
    }

    function handleRememberDevice() {
        try {
            postWithCsrf("/user/remember_device", "", {fetching: fetching}).then(response => {
                if(response.ok) {
                    setDisplayRememberDevice(false);
                    setRemLoadState(LoadState.LOAD_SUCCESS);
                }
                else {
                    throw new Error("Response code: " + response.status);
                }
            }).catch(error => {
                console.log(error);
                setRemLoadState(LoadState.LOAD_ERROR);
            });

            // Calling setConfirmLoadState() after postWithCsrf() as it could throw an exception
            setRemLoadState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }


    // EFFECTS:

    useLayoutEffect(() => {
        boardHeightRef.current = boardHeight;
    }, [boardHeight]);

    useLayoutEffect(() => {
        if(contentRef.current) {
            const resizeObserver = new ResizeObserver(entries => {
                for (let entry of entries)
                {
                    const { height } = entry.contentRect;
                    setContentHeight(height);
                }
            });
        
            resizeObserver.observe(contentRef.current);
        
            return () => resizeObserver.disconnect();
        }
    });

    useResizeListener((windowWidth, windowHeight, regular) => {
        // We only want to react to visualViewport API events if the new window height is higher than
        // the current board height to react to keyboard disappearance only (not to appearance) on mobile devices
        // (especially on Safari IOS).
        if(regular || windowHeight > boardHeightRef.current) {
            setBoardHeight(windowHeight);
        }

        if(regular) {
            // Only for triggering a rerender because of scrollOnAppBoxFunc() for app_box
            setInnerWidth(windowWidth);
        }
    });

    useEffect(() => {
        localeRef.current = locale;
    }, [locale]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        let newLocale = params.get("lang");

        if(!newLocale) {
            newLocale = "de";
        }
        
        if(localeRef.current != newLocale || initRef.current) {
            setLocale(newLocale);
            localeRef.current = newLocale;

            loadLocaleFunc(newLocale);
        }

        initRef.current = false;
    }, [location.search]);

    useEffect(() => {
        function handleScroll(e) {
            e.preventDefault();
            curScrollPos.current = -scrollDummyRef.current.getBoundingClientRect().top;
        }

        const scrollEle = getScrollEle();

        if(scrollEle) {
            scrollEle.addEventListener("scroll", handleScroll);
            return () => scrollEle.removeEventListener("scroll", handleScroll);
        }
        
    }, [window.innerWidth, location.pathname, scrollOnAppBox, loadState]);

    useEffect(() => {
        if(noScroll) {
            document.documentElement.style.overflow = "hidden";
        }
        else {
            document.documentElement.style.overflow = "auto";
        }
    }, [noScroll]);

    useLayoutEffect(() => {
        function isLimitReached() {
            const scrollEle = getScrollEle();

            const scrollEleRect = scrollEle.getBoundingClientRect();
            const scrollEleHeight = scrollEleRect.bottom - scrollEleRect.top;

            const scrollBottom = scrollEleHeight + scrollEle.scrollTop;
            return scrollBottom > scrollLimitYRef.current ? scrollLimitYRef.current - scrollEleHeight : -1;
        }

        function limitScroll(e) {
            if(scrollLimitYRef.current !== -1 && isLimitReached() !== -1) {
                if(scrollLimitTimeout.current) {
                    clearTimeout(scrollLimitTimeout.current);
                }
                
                scrollLimitTimeout.current = setTimeout(() => {
                    // Check again as the user might have scrolled up again
                    const y = isLimitReached();

                    if(y !== -1) {
                        scrollToPlain2(y, y);
                    }

                    scrollLimitTimeout.current = null;
                }, 500);
            }
        }

        const scrollEle = getScrollEle();

        if(scrollEle) {
            scrollEle.addEventListener("scroll", limitScroll, { passive: false });
            return () => scrollEle.removeEventListener("scroll", limitScroll);
        }
    });


    // RENDER:

    return (
        <div ref={appBoxRef} id="app_box" style={ scrollOnAppBoxFunc() ? {overflow: "auto"} : {}}>
            <div ref={scrollDummyRef}></div>

            <div style={{ visibility: (loadingCounter > 0 ? "visible" : "hidden")}}>
                <Loading fixed={true} management={props.management} />
            </div>

            { loadState === LoadState.LOAD_SUCCESS ?

                /* Manually setting the height of app from window.innerHeight as 100vh does not work properly on Safari IOS
                    (moving URL bar is not regarded) */
                /* DO NOT change the id app_box as it es being used to determine the actual viewport width without the scrollbar */
                <div ref={boardRef} id="app" style={{height: boardHeight}}>
                    <div className={ "overlay" + (overlay ? " overlay_visible": "" )}></div>
                    <div className={ "overlay overlay_solid" + (overlaySolid ? " overlay_solid_visible": "" )}></div>
                    <div className={ "overlay overlay_solid_white" + (overlaySolidWhite ? " overlay_solid_white_visible": "" )}></div>

                    { displaySystemLabel && (system === "staging") ?
                        <div className="staging_overlay">
                            <div className="staging_overlay_text">Staging</div>
                            <div className="staging_overlay_close_btn_box" onClick={() => { setDisplaySystemLabel(!displaySystemLabel)}}>
                                <img className="staging_overlay_close_btn_box" src="/common/icon_close.svg" />
                            </div>
                        </div>
                    :
                        null
                    }

                    <div ref={contentRef} className={"content" + (noContentPad ? "" : " content_pad")}>
                        <div className={ "overlay_content" + (overlay ? " overlay_content_visible": "" )} style={{height: contentHeight}}></div>

                        <OverlayContext.Provider value={{ overlay, setOverlay, overlaySolid, setOverlaySolid, overlaySolidWhite, setOverlaySolidWhite,
                                incrLoadingCounter, decrLoadingCounter,
                                setErrorPopupDisplayed, setErrorPopupInfo }}>
                            <ScrollContext.Provider value={{ scrollTo, scrollToPlain, scrollOffset, noContentPad, setNoContentPad, scrollOnAppBox, setScrollOnAppBox,
                                    getCurrentScrollPos, addScrollListener, removeScrollListener, stopScroll, startScroll, setNoScroll, setScrollLimitY }}
                            >
                                <LocaleContext.Provider value={{ msgs, setMsgs, locale }}>
                                    <LoginContext.Provider value={{ loggedIn, setLoggedIn, username, setUsername,
                                            password, setPassword, userType, setUserType, checkingLogin, setCheckingLogin,
                                            twoFactorAuthType, setTwoFactorAuthType,
                                            twoFactorAuthMobileNo, setTwoFactorAuthMobileNo,
                                            nextSmsCountS, setNextSmsCountS,
                                            notifications, setNotifications,
                                            displayRememberDevice, setDisplayRememberDevice,
                                            mailConfirmed, setMailConfirmed, twoFactorAuthEnabled, setTwoFactorAuthEnabled,
                                            navigateUrlAfterLogin }}>
                                            
                                        <StateContext.Provider value={{ fetching }}>
                                            <NavigateLockContext.Provider value={{locked, lockedRef, setLockedFunc, lockAnimationRunning, setLockAnimationRunning, handleLockProtectedClick}}>

                                                {props.children}

                                                <ClosePopup fixed={true} helpDisplayed={errorPopupDisplayed} onClose={() => setErrorPopupDisplayed(false)}
                                                        content={
                                                            <div className="error_popup_content">
                                                                <div className="head profile_head">{errorPopupInfo.title}</div>
                                                                <div className="text_2 margin_top_low_low">{errorPopupInfo.text}</div>
                                                            </div>
                                                        }
                                                />

                                            </NavigateLockContext.Provider>

                                            <ClosePopup fixed={true} helpDisplayed={displayRememberDevice} onClose={() => setDisplayRememberDevice(false)}
                                                    content={
                                                        <div className="remember_device_popup_content">
                                                            <div className="head profile_head">{msgs.REMEMBER_DEVICE_HEAD}</div>
                                                            <div className="text_2 margin_top_low_low">{msgs.REMEMBER_DEVICE_DESCR}</div>

                                                            <div className="remember_device_btns">
                                                                <LoadButton text={msgs.REMEMBER_DEVICE} clickable={true} onClick={handleRememberDevice}
                                                                        saving={remLoadState === LoadState.LOADING}
                                                                        notSaved={remLoadState === LoadState.LOAD_ERROR}
                                                                        errText={msgs.ERROR} />

                                                                <LoadButton text={msgs.DO_NOT_REMEMBER_DEVICE} white={true} clickable={true}
                                                                        onClick={() => setDisplayRememberDevice(false)}
                                                                        saving={false}
                                                                        notSaved={false}
                                                                        errText={""} />
                                                            </div>
                                                        </div>
                                                    }
                                            />

                                        </StateContext.Provider>
                                    </LoginContext.Provider>
                                </LocaleContext.Provider>
                            </ScrollContext.Provider>
                        </OverlayContext.Provider>
                    </div>
                    
                </div>
            :
                null
            }

            <ScrollBarAdapter />
        </div>
    );
}

export default App;
